import { Environment } from '@src/config/environment';
import { getJob } from './JobService';
import { getApiClientByBodyType } from '../ApiClients/getApiClientByBodyType';
import { type JobImageDTO } from '@funnellab/shared/src/dtos/creativeBuilder/JobImageDTO';

export type JobCreative = {
  category: 'Post' | 'Story' | 'StockImage' | null;
  fileId: string;
  fileName: string;
  fileType?: string | null;
  fileSize?: number | null;
  width?: number | null;
  height?: number | null;
  isFavorite: boolean;
};

export const getPostScene = async (jobId: string) => {
  const job = await getJob(jobId);
  const sceneFile = job.scenes.post;
  return sceneFile;
};

export const getStoryScene = async (jobId: string) => {
  const job = await getJob(jobId);
  const sceneFile = job.scenes.story;
  return sceneFile;
};

export const getPostSceneUrl = (jobId: string) => {
  return `${Environment.DIRECTUS_API_URL}/jobs/${jobId}/scenes/post`;
};

export const getStorySceneUrl = (jobId: string) => {
  return `${Environment.DIRECTUS_API_URL}/jobs/${jobId}/scenes/story`;
};

export const updatePostScene = async (jobId: string, fileContent: string) => {
  const data = new FormData();
  const file = createFileFromContent(fileContent, 'post', jobId);
  data.append('file', file);

  return await getApiClientByBodyType('formData').patch(
    `jobs/${jobId}/scenes/post`,
    data,
  );
};

export const updateStoryScene = async (jobId: string, fileContent: string) => {
  const data = new FormData();
  const file = createFileFromContent(fileContent, 'story', jobId);
  data.append('file', file);

  return await getApiClientByBodyType('formData').patch(
    `jobs/${jobId}/scenes/story`,
    data,
  );
};

export async function createPostScene(jobId: string, fileContent: string) {
  const data = new FormData();
  const file = createFileFromContent(fileContent, 'post', jobId);
  data.append('file', file);

  return await getApiClientByBodyType('formData').post(
    `jobs/${jobId}/scenes/post`,
    data,
  );
}

export async function createStoryScene(jobId: string, fileContent: string) {
  const data = new FormData();
  const file = createFileFromContent(fileContent, 'story', jobId);
  data.append('file', file);

  return await getApiClientByBodyType('formData').post(
    `jobs/${jobId}/scenes/story`,
    data,
  );
}

export async function generateCreativesFromScene(
  jobId: string,
  type: 'post' | 'story',
) {
  return getApiClientByBodyType('json').post(
    `jobs/${jobId}/scenes/${type}/generateCreatives`,
    {},
  );
}

function createFileFromContent(
  fileContent: string,
  type: 'post' | 'story',
  jobId: string,
): File {
  return new File([fileContent], `${type}_${jobId}.scene`, {
    type: 'application/octet-stream',
  });
}

const mapJobImageDTOToJobCreative = (dto: JobImageDTO): JobCreative => {
  return {
    category: dto.category,
    fileId: dto.file_details.id,
    fileName: dto.file_details.filename_download,
    fileType: dto.file_details.type,
    fileSize: dto.file_details.filesize,
    width: dto.file_details.width,
    height: dto.file_details.height,
    isFavorite: dto.isFavorite,
  };
};

export const getJobCreatives = async (
  jobId: string,
  type?: 'post' | 'story' | 'stock_image',
): Promise<JobCreative[]> => {
  const queryParam = type ? `?type=${type}` : '';
  const response = await getApiClientByBodyType('json').get<JobImageDTO[]>(
    `jobs/${jobId}/creatives${queryParam}`,
  );

  return response.map(mapJobImageDTOToJobCreative);
};

export const generateStockImagesForJob = async (
  jobId: string,
): Promise<void> => {
  await getApiClientByBodyType('json').put(`jobs/${jobId}/stockImages`, {});
};
